import { useEffect } from "react";
import { usePostPropertyCottageMutation } from "web_myplann/Api/Portfolio/Mutations/usePostPropertyCottageMutation";
import { ClientPropertyTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { BuildingCreateFormFields } from "web_myplann/Portfolio/Forms/BuildingShared/BuildingCreateFormFields";
import type { BuildingFormInput } from "web_myplann/Portfolio/Forms/BuildingShared/buildingFormSchema";
import { COTTAGE_FORM_DEFAULT_VALUES } from "web_myplann/Portfolio/Forms/CottageForm/cottageFormDefaults";
import { getCottageFormSchema } from "web_myplann/Portfolio/Forms/CottageForm/cottageFormSchema";
import type { RealEstateType } from "web_myplann/Portfolio/Forms/RealEstateForm/realEstateTypes";
import { PortfolioFormShell } from "web_myplann/Portfolio/PortfolioShared/PortfolioFormShell";
import { useCreatePortfolioForm } from "web_myplann/Portfolio/PortfolioShared/useCreatePortfolioForm";

type CreateCottageFormProps = {
	onTypeChange: (type: RealEstateType) => void;
	registerSnapshot: (getValues: () => BuildingFormInput) => void;
	initialValues: BuildingFormInput | undefined;
};

export const CreateCottageForm = ({
	onTypeChange,
	registerSnapshot,
	initialValues,
}: CreateCottageFormProps) => {
	const { t } = useResource();

	const { mutate, isPending } = usePostPropertyCottageMutation();

	const { form, onSubmit } = useCreatePortfolioForm({
		schema: getCottageFormSchema(t),
		defaultValues: initialValues ?? COTTAGE_FORM_DEFAULT_VALUES,
		mutate: ({ householdArea, ...values }, options) => mutate(values, options),
		successRoute: Routing.Portfolio.PortfolioAddRealEstateSuccess.path(),
	});

	useEffect(() => {
		registerSnapshot(form.getValues);
	}, [registerSnapshot, form.getValues]);

	return (
		<PortfolioFormShell
			onSubmit={onSubmit}
			submitLabel={t(Resources.Portfolio.AddRealEstate.submit)}
			disabled={!form.formState.isDirty}
			loading={isPending}
		>
			<BuildingCreateFormFields
				control={form.control}
				type={ClientPropertyTypeEnum.Cottage}
				onTypeChange={onTypeChange}
			/>
		</PortfolioFormShell>
	);
};
